import {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {useAuth} from '../../../../config/AuthProvider/authProvider';
import {
    ACTIVE_IN_PROGRESS_LOAN,
    DOCUMENT_TYPES,
    LOAN_CARD_INFO,
} from '../../../../config/cacheKey';
import LayoutWithoutDrawer from '../../../../layouts/LayoutWithoutDrawer';
import {IDocumentTypeHints} from '../../../../models/basicInformation.model';
import {IGetLoansInfoResponse} from '../../../../models/registration.model';
import {changeRouteType} from '../../../../redux/handleBackBtn/handleBackBtnSlice';
import {setHeaderTitle} from '../../../../redux/headerTitle/headerTitleSlice';
import {getRegistrationForms} from '../../../../services/registration.service';
import LoanWorkflowGuard from './LoanWorkflowGuard';
import {GetDocumentTypes} from '../../../../services/basicInformation.service';
import {GetLoanInfo} from '../../../../services/loan.service';
import BasicInforContainer from '../../../basicInformation/basicInfoContainer';
import Accept from '../../../loanResult/accept';
import Reject from '../../../loanResult/reject';
import WaitForBank from '../../../loanResult/waitForBank';
import AdditionalStep from '../../../requestSteps/additionInfo/additionalStep';
import CheckDocument from '../../../requestSteps/cheques/checkDocument/checkDocument';
import DrawerBody from '../../../requestSteps/drawerBody/drawerBody';
import WaitCustomersDocument from '../../../requestSteps/form/waitCustomersDocument';
import OnlinePrePayment from '../../../requestSteps/inquiryFee/resultPayment/onlinePrePayment';
import WaitForInquiry from '../../../requestSteps/inquiryFee/waitForInquiry';
import WaitSign from '../../../signContract/waitSign';
import LoanTemplateContainer from '../Loans/loanTemplate/LoanTemplateContainer';
import PrePayment from '../Loans/PrePayment';
import {setLoanCalculationResult} from '../../../../redux/loanCalculationResult/loanCalculationResultSlice';
import {Navigate, Route, Routes, useLocation} from 'react-router-dom';

export default function ContainerRegisterLoan({
    setTabChild,
    refKey,
}: {
    setTabChild: (id: number) => void;
    refKey: string;
}) {
    const {referenceKey} = useAuth();
    const activeReferenceKey = refKey || referenceKey;
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const location = useLocation();
    const calculation = useSelector(
        (state: RootState) => state.loanCalculationResult
    );
    const [revision, setRevision] = useState(0);
    // Callback entry only opens the verifier. The receipt API still validates the token.
    const [paymentCallback, setPaymentCallback] = useState(() => {
        const params = queryClient.getQueryData('URL_PARAMS') as
            | {token?: URLSearchParams}
            | undefined;
        return (
            queryClient.getQueryData('ONLINE_PREPAYMENT') ===
                'onlinePrepayment' && !!params?.token?.get('t')
        );
    });
    const [publishedInfo, setPublishedInfo] = useState<IGetLoansInfoResponse>();
    const setStep = (_value?: unknown) => {
        setPaymentCallback(false);
        queryClient.setQueryData('ONLINE_PREPAYMENT', '');
        setRevision((value) => value + 1);
    };
    const loanInfo = useQuery(
        [LOAN_CARD_INFO, activeReferenceKey, revision],
        async () => {
            const data = await GetLoanInfo({referenceKey: activeReferenceKey!});
            if (
                !data ||
                data.referenceKey !== activeReferenceKey ||
                !data.loanRequestStatusId
            ) {
                throw new Error('Invalid loan workflow response');
            }
            return data;
        },
        {
            enabled: !!activeReferenceKey && !paymentCallback,
            staleTime: 0,
            cacheTime: 0,
            retry: false,
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );
    const stepMain = activeReferenceKey
        ? loanInfo.data?.loanRequestStatusId || ''
        : 'calculator';
    const forms = useQuery(
        ['loanWorkflowForms', activeReferenceKey, revision],
        async () => {
            const result = await getRegistrationForms({
                referenceKey: activeReferenceKey!,
            });
            if (!Array.isArray(result))
                throw new Error('Invalid loan forms response');
            return result;
        },
        {
            enabled:
                !paymentCallback &&
                stepMain === 'WaitForCustomerDocument' &&
                !loanInfo.isFetching,
            cacheTime: 0,
            retry: false,
            refetchOnWindowFocus: false,
        }
    );
    useEffect(() => {
        if (loanInfo.data && !loanInfo.isFetching && !loanInfo.isError) {
            queryClient.setQueryData(LOAN_CARD_INFO, loanInfo.data);
            sessionStorage.setItem(
                LOAN_CARD_INFO,
                JSON.stringify(loanInfo.data)
            );
            dispatch(setLoanCalculationResult(loanInfo.data));
            setPublishedInfo(loanInfo.data);
        }
    }, [
        loanInfo.data,
        loanInfo.isFetching,
        loanInfo.isError,
        dispatch,
        queryClient,
    ]);
    useEffect(() => {
        if (!activeReferenceKey && !paymentCallback) {
            sessionStorage.removeItem(LOAN_CARD_INFO);
            queryClient.removeQueries(LOAN_CARD_INFO, {exact: true});
        }
    }, [activeReferenceKey, paymentCallback, queryClient]);
    const {mutate: documentTypeMutation} = useMutation(GetDocumentTypes, {
        onSuccess: (data: IDocumentTypeHints[]) => {
            localStorage.setItem(DOCUMENT_TYPES, JSON.stringify(data));
        },
    });
    useEffect(() => {
        dispatch(changeRouteType({routeType: 'loans'}));
        dispatch(
            setHeaderTitle({
                header: ' ثبت درخواست جدید',
                parentRoute: null,
                color: 'var(--header-color)',
            })
        );
        documentTypeMutation();
        return () => {
            sessionStorage.setItem(
                'HAS_INPROGRESS',
                JSON.stringify({inProgress: false})
            );
        };
    }, []);

    const loanTemplates = (
        <LayoutWithoutDrawer>
            <LoanTemplateContainer
                setStep={setStep}
                setTabChild={setTabChild}
            />
        </LayoutWithoutDrawer>
    );
    const basicInfo = (
        <LayoutWithoutDrawer>
            <BasicInforContainer
                referenceKey={activeReferenceKey!}
                setStep={setStep}
                back="calculator"
            />
        </LayoutWithoutDrawer>
    );
    const inquiry = (
        <DrawerBody>
            <WaitForInquiry
                referenceKey={activeReferenceKey!}
                setStep={setStep}
                stepMain={stepMain}
            />
        </DrawerBody>
    );
    const documents = (
        <WaitCustomersDocument
            referenceKey={activeReferenceKey!}
            setStep={setStep}
        />
    );
    const contract = (
        <DrawerBody>
            <WaitSign
                referenceKey={activeReferenceKey!}
                setStep={setStep}
                stepMain={stepMain}
            />
        </DrawerBody>
    );

    if (paymentCallback) {
        if (location.pathname !== '/loans/request/onlinePrepayment') {
            return <Navigate to="/loans/request/onlinePrepayment" replace />;
        }
        return (
            <LayoutWithoutDrawer>
                <OnlinePrePayment setStep={setStep} />
            </LayoutWithoutDrawer>
        );
    }
    const pending =
        !!activeReferenceKey &&
        (loanInfo.isLoading ||
            loanInfo.isFetching ||
            (!loanInfo.isError && publishedInfo !== loanInfo.data) ||
            (stepMain === 'WaitForCustomerDocument' &&
                (forms.isLoading || forms.isFetching)));
    return (
        <LoanWorkflowGuard
            status={stepMain}
            pending={pending}
            creation={{
                templateSelected:
                    !!queryClient.getQueryData('LOAN_TEMPLATE_KEY'),
                calculationReady: !!(
                    calculation?.financialSupplier &&
                    calculation?.loanTemplateId &&
                    calculation?.stepSpaceId &&
                    calculation?.loanAmount > 0
                ),
            }}
            error={
                !!activeReferenceKey &&
                (loanInfo.isError ||
                    (stepMain === 'WaitForCustomerDocument' && forms.isError))
            }
            retry={() => setStep()}
            availableForms={forms.data
                ?.filter((form) => form.formState.toLowerCase() !== 'completed')
                .map((form) => form.form)}
        >
            <div>
                <Routes>
                    <Route
                        index
                        element={
                            stepMain === 'WaitForCustomerDocument'
                                ? documents
                                : loanTemplates
                        }
                    />
                    <Route path="confirmLoanTemplate" element={loanTemplates} />
                    <Route path="createNewRequest" element={loanTemplates} />
                    <Route path="TemporaryRegistered" element={loanTemplates} />
                    <Route path="additionalInfo" element={basicInfo} />
                    <Route path="basickInfo" element={basicInfo} />
                    <Route path="fee" element={inquiry} />
                    <Route path="otp" element={inquiry} />
                    <Route path="inquiryResult" element={inquiry} />
                    <Route
                        path="requestApproval"
                        element={
                            <DrawerBody>
                                <CheckDocument
                                    referenceKey={activeReferenceKey!}
                                    setStep={setStep}
                                />
                            </DrawerBody>
                        }
                    />
                    <Route
                        path="salaryDeduction"
                        element={
                            <DrawerBody>
                                <AdditionalStep
                                    referenceKey={activeReferenceKey!}
                                    setStep={setStep}
                                />
                            </DrawerBody>
                        }
                    />
                    <Route
                        path="documentApproval"
                        element={
                            <DrawerBody>
                                <CheckDocument
                                    referenceKey={activeReferenceKey!}
                                    setStep={setStep}
                                    stepMain={stepMain}
                                />
                            </DrawerBody>
                        }
                    />
                    <Route
                        path="prepayment"
                        element={
                            <DrawerBody>
                                <PrePayment
                                    referenceKey={activeReferenceKey!}
                                    setStep={setStep}
                                    stepMain={stepMain}
                                />
                            </DrawerBody>
                        }
                    />
                    <Route path="viewContract" element={contract} />
                    <Route path="signContract" element={contract} />
                    <Route
                        path="sendToBank"
                        element={
                            <DrawerBody>
                                <WaitForBank />
                            </DrawerBody>
                        }
                    />
                    <Route
                        path="accepted"
                        element={
                            <DrawerBody>
                                <Accept
                                    setTabChild={setTabChild}
                                    referenceKey={activeReferenceKey!}
                                    setStep={setStep}
                                    stepMain={stepMain}
                                />
                            </DrawerBody>
                        }
                    />
                    <Route
                        path="rejected"
                        element={
                            <DrawerBody>
                                <Reject setStep={setStep} />
                            </DrawerBody>
                        }
                    />
                    <Route path="*" element={documents} />
                </Routes>
            </div>
        </LoanWorkflowGuard>
    );
}
